import { useEffect, useRef } from 'react';

// Global connection manager - maintains one EventSource per URL with multiple subscribers
class SSEConnectionManager {
  constructor() {
    this.connections = new Map(); // Map<url, { eventSource, subscribers, reconnectAttempts, reconnectTimeout }>
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
  }

  subscribe(url, onMessage, onError) {
    const connection = this.connections.get(url) || {
      eventSource: null,
      subscribers: new Set(),
      reconnectAttempts: 0,
      reconnectTimeout: null,
      isIntentionallyClosing: false,
    };

    const subscriberId = Symbol();
    connection.subscribers.add({
      id: subscriberId,
      onMessage,
      onError,
    });

    this.connections.set(url, connection);

    // Create connection if it doesn't exist
    if (!connection.eventSource || connection.eventSource.readyState === EventSource.CLOSED) {
      this.connect(url);
    }

    // Return unsubscribe function
    return () => {
      const conn = this.connections.get(url);
      if (conn) {
        // Remove this subscriber
        conn.subscribers.forEach((sub) => {
          if (sub.id === subscriberId) {
            conn.subscribers.delete(sub);
          }
        });

        // If no more subscribers, close the connection
        if (conn.subscribers.size === 0) {
          this.disconnect(url);
          this.connections.delete(url);
        }
      }
    };
  }

  connect(url) {
    const connection = this.connections.get(url);
    if (!connection) return;

    // Don't create if already connecting or open
    if (connection.eventSource) {
      const state = connection.eventSource.readyState;
      if (state === EventSource.CONNECTING || state === EventSource.OPEN) {
        console.log(`SSE: Connection to ${url} already exists (state: ${state === EventSource.CONNECTING ? 'CONNECTING' : 'OPEN'})`);
        return;
      }
    }

    try {
      // Clean up existing connection
      if (connection.eventSource) {
        connection.isIntentionallyClosing = true;
        connection.eventSource.onerror = null;
        connection.eventSource.onopen = null;
        connection.eventSource.onmessage = null;
        connection.eventSource.close();
        connection.isIntentionallyClosing = false;
      }

      // Clear any pending reconnection
      if (connection.reconnectTimeout) {
        clearTimeout(connection.reconnectTimeout);
        connection.reconnectTimeout = null;
      }

      console.log(`SSE: Creating shared connection to ${url} (${connection.subscribers.size} subscriber(s))`);
      const eventSource = new EventSource(url);
      connection.eventSource = eventSource;

      eventSource.onopen = () => {
        if (!connection.isIntentionallyClosing) {
          console.log(`SSE: Shared connection to ${url} opened (${connection.subscribers.size} subscriber(s))`);
          connection.reconnectAttempts = 0;
        }
      };

      eventSource.onmessage = (event) => {
        if (connection.isIntentionallyClosing) return;

        try {
          const data = JSON.parse(event.data);
          // Broadcast to all subscribers
          connection.subscribers.forEach((subscriber) => {
            if (subscriber.onMessage) {
              subscriber.onMessage(data);
            }
          });
        } catch (error) {
          if (event.data === 'connected') {
            console.log(`SSE: Shared connection to ${url} established`);
          } else {
            console.error('SSE: Error parsing message:', error);
          }
        }
      };

      eventSource.onerror = (error) => {
        if (connection.isIntentionallyClosing) return;

        if (eventSource.readyState === EventSource.CLOSED) {
          console.error(`SSE: Shared connection to ${url} closed`);
          
          // Notify all subscribers
          connection.subscribers.forEach((subscriber) => {
            if (subscriber.onError) {
              subscriber.onError(error);
            }
          });

          // Attempt reconnect if we still have subscribers
          if (connection.subscribers.size > 0 && !connection.isIntentionallyClosing) {
            if (connection.reconnectAttempts < this.maxReconnectAttempts) {
              connection.reconnectAttempts += 1;
              console.log(`SSE: Reconnecting shared connection to ${url} (attempt ${connection.reconnectAttempts}/${this.maxReconnectAttempts})...`);
              
              connection.reconnectTimeout = setTimeout(() => {
                if (connection.subscribers.size > 0 && !connection.isIntentionallyClosing) {
                  this.connect(url);
                }
              }, this.reconnectDelay);
            } else {
              console.error(`SSE: Max reconnection attempts reached for ${url}`);
              connection.subscribers.forEach((subscriber) => {
                if (subscriber.onError) {
                  subscriber.onError(new Error('Failed to reconnect to SSE server'));
                }
              });
            }
          }
        } else if (eventSource.readyState === EventSource.CONNECTING) {
          console.log(`SSE: Shared connection to ${url} connecting...`);
        }
      };
    } catch (error) {
      console.error(`SSE: Failed to create shared connection to ${url}:`, error);
      connection.subscribers.forEach((subscriber) => {
        if (subscriber.onError) {
          subscriber.onError(error);
        }
      });
    }
  }

  disconnect(url) {
    const connection = this.connections.get(url);
    if (!connection) return;

    console.log(`SSE: Disconnecting shared connection to ${url}`);
    connection.isIntentionallyClosing = true;

    if (connection.eventSource) {
      connection.eventSource.onerror = null;
      connection.eventSource.onopen = null;
      connection.eventSource.onmessage = null;
      connection.eventSource.close();
      connection.eventSource = null;
    }

    if (connection.reconnectTimeout) {
      clearTimeout(connection.reconnectTimeout);
      connection.reconnectTimeout = null;
    }

    connection.isIntentionallyClosing = false;
  }
}

// Singleton instance
const connectionManager = new SSEConnectionManager();

// Handle visibility changes globally
let visibilityHandlerAdded = false;
const handleVisibilityChange = () => {
  if (document.hidden) {
    // Tab hidden - pause all connections
    connectionManager.connections.forEach((connection, url) => {
      if (connection.eventSource && connection.eventSource.readyState !== EventSource.CLOSED) {
        connectionManager.disconnect(url);
      }
    });
  } else {
    // Tab visible - reconnect all connections that have subscribers
    connectionManager.connections.forEach((connection, url) => {
      if (connection.subscribers.size > 0) {
        connection.reconnectAttempts = 0;
        connectionManager.connect(url);
      }
    });
  }
};

// Add visibility handler once
if (typeof document !== 'undefined' && !visibilityHandlerAdded) {
  document.addEventListener('visibilitychange', handleVisibilityChange);
  visibilityHandlerAdded = true;
}

/**
 * Custom hook for Server-Sent Events (SSE) connection
 * Uses a shared connection manager so multiple components can share the same EventSource
 * @param {string} workerUrl - URL of the SSE worker endpoint
 * @param {Function} onMessage - Callback function called when a message is received
 * @param {Function} onError - Optional error callback
 * @returns {Object} - Object with connection status
 */
export function useSSE(workerUrl, onMessage, onError) {
  const unsubscribeRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);

  // Keep callbacks in refs to avoid re-subscribing when they change
  useEffect(() => {
    onMessageRef.current = onMessage;
    onErrorRef.current = onError;
  }, [onMessage, onError]);

  useEffect(() => {
    if (!workerUrl) {
      console.warn('SSE: Worker URL not provided');
      return;
    }

    // Build full URL
    let fullUrl;
    if (workerUrl.startsWith('http')) {
      fullUrl = workerUrl.endsWith('/events') ? workerUrl : `${workerUrl}/events`;
    } else if (workerUrl.includes('/events')) {
      fullUrl = workerUrl;
    } else {
      console.warn(`SSE: Invalid workerUrl format: ${workerUrl}. Expected full URL.`);
      fullUrl = `${workerUrl}/events`;
    }

    // Subscribe to shared connection
    console.log(`SSE: Subscribing to shared connection: ${fullUrl}`);
    unsubscribeRef.current = connectionManager.subscribe(
      fullUrl,
      (data) => {
        if (onMessageRef.current) {
          onMessageRef.current(data);
        }
      },
      (error) => {
        if (onErrorRef.current) {
          onErrorRef.current(error);
        }
      }
    );

    // Cleanup: unsubscribe when component unmounts or URL changes
    return () => {
      console.log(`SSE: Unsubscribing from shared connection: ${fullUrl}`);
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [workerUrl]);
}

/**
 * Get the worker URL from environment variable or construct from current origin
 * @returns {string|null} - Worker URL or null if not available
 */
export function getWorkerUrl() {
  // Check for explicit environment variable
  if (import.meta.env.VITE_WORKER_URL) {
    return import.meta.env.VITE_WORKER_URL;
  }

  // In production, if worker is deployed as a separate service,
  // you'll need to set VITE_WORKER_URL in your environment variables
  // For now, return null to gracefully handle missing URL
  return null;
}
